
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { NotificationManager } from '@/managers/notification-manager';
import { ChatMessageDeleteNotification, ChatNotification } from '@/model/notification';
import PlayerStore from '@/store/player';
import ClanStore from '@/store/clan';
import { MessageChat } from '@/store/chat/types';
import { Settings } from '@/constants/settings';
import PcSideChatMessage from '@/components/chat/PcSideChatMessage.vue';
import { ChatLiveUpdatesController, syncChatMessages } from '@/utils/chat-live-updates';
import { formatChatSentTime } from '@/utils/chat-time-utils';

@Component({
  components: { PcSideChatMessage },
})
export default class PcSideChatPanel extends Vue {
  private readonly CHAT_FORM_ACCESS_LEVEL = 10;
  private readonly smileyPrefix: string = Settings.SMILEY_MESSAGE_PREFIX;
  private readonly forwardedMessagePrefix: string = Settings.FORWARDED_MESSAGE_PREFIX;
  private readonly clanMinLevel: number = Settings.CLAN_MIN_LEVEL;
  private readonly liveUpdatesIntervalMs: number = 5000;

  @Prop({ required: true }) private roomType!: string;
  @Prop({ required: true }) private title!: string;
  @Prop({ default: '' }) private placeholder!: string;
  @Prop({ default: '' }) private sideClass!: string;
  @Prop({ default: true }) private open!: boolean;

  @Action private fetchChatList!: (payload: { roomType: string }) => Promise<void>;
  @Action private fetchLatestChatMessages!: (payload: { roomType: string }) => Promise<void>;
  @Action private postChatMessage!: (payload: { roomType: string; text: string }) => Promise<void>;
  @Action private clearChatMessages!: (payload: { roomType: string }) => Promise<void>;
  @Action private deleteChatMessage!: (payload: { roomType: string; id: number }) => Promise<void>;
  @Getter('chatMessages') private chatMessagesByRoom!: (roomType: string) => MessageChat[];

  private text: string = '';
  private smileyPickerOpen: boolean = false;
  private quotedMessage: MessageChat | null = null;
  private initialLoadAttempts: number = 0;
  private initialLoadTimer: number | null = null;
  private clanInfoLoadAttempted: boolean = false;
  private timeTick: number = Date.now();
  private readonly liveUpdates = new ChatLiveUpdatesController({
    intervalMs: this.liveUpdatesIntervalMs,
    onTick: () => {
      this.timeTick = Date.now();
    },
    sync: () => this.syncLatestMessages(),
  });

  private readonly smileys: string[] = [
    '😀', '😁', '😂', '🤣', '😅', '😆', '😊', '😉', '🙃', '😇',
    '😍', '😘', '😋', '😜', '🤪', '😎', '🤔', '🤨', '😐', '😴',
    '😢', '😭', '😡', '🤯', '😱',
    '🙏', '👍', '👎', '👏', '🤝', '💪', '👀',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔',
    '🎉', '✨', '🔥', '💯',
  ];

  get isClanRoom() {
    return this.roomType === Settings.CLAN_CHAT_ROOM;
  }

  get showClanEmptyMessage() {
    if (!this.isClanRoom) return false;
    const p = PlayerStore.getPlayer();
    // Пока игрок не загружен — не показываем “не в клане”, чтобы не мигало на старте.
    if (!p) return false;
    return !p.clanId || p.level < this.clanMinLevel;
  }

  get messages(): MessageChat[] {
    if (this.showClanEmptyMessage) return [];
    // В `chat.html` визуально новые сообщения снизу, поэтому сортируем ASC по времени.
    return [...this.chatMessagesByRoom(this.roomType)].sort((a, b) => a.time - b.time);
  }

  get canSend() {
    return this.text.trim().length > 0;
  }

  get canWrite() {
    const p = PlayerStore.getPlayer();
    if (!p) return false;
    if (this.isClanRoom && (!p.clanId || p.level < this.clanMinLevel)) return false;
    return p.level >= this.CHAT_FORM_ACCESS_LEVEL;
  }

  get placeholderText() {
    if (this.showClanEmptyMessage) return 'Вы пока не в клане.';
    return this.canWrite ? this.placeholder : 'Чтобы отправить сообщение нужен 10-й уровень!';
  }

  get quotePreviewText() {
    if (!this.quotedMessage) return '';
    const m = this.quotedMessage;
    // В боковых чатах в блоке цитирования показываем только ник, без текста сообщения
    return `${m.authorName}`;
  }

  private formatMessageTime(message: MessageChat): string {
    void this.timeTick;
    return formatChatSentTime(message?.time, message?.sentAt || '');
  }

  private clearQuote() {
    this.quotedMessage = null;
  }

  protected created() {
    this.setChatListener();
    this.setMessageDeleteListener();
  }

  protected async mounted() {
    this.initialLoadAttempts = 0;
    if (this.open) {
      this.loadInitialMessages();
    }
    this.syncLiveUpdatesState();

    document.addEventListener('click', this.onDocumentClick);
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.initialLoadTimer !== null) {
        clearTimeout(this.initialLoadTimer);
        this.initialLoadTimer = null;
      }
      this.liveUpdates.stop();
      this.clearChatMessages({ roomType: this.roomType });
    });
  }

  @Watch('open')
  private onOpenChanged(next: boolean) {
    if (!next) {
      if (this.initialLoadTimer !== null) {
        clearTimeout(this.initialLoadTimer);
        this.initialLoadTimer = null;
      }
    }
    this.syncLiveUpdatesState();
    if (!next) return;
    this.initialLoadAttempts = 0;
    this.loadInitialMessages();
    this.$nextTick(() => this.scrollToBottom());
  }

  private isExpectedInitialLoadError(e: unknown): boolean {
    if (e instanceof Error && e.message === 'player_not_ready') return true;
    const status = (e as { response?: { status?: number } })?.response?.status;
    return status === 401;
  }

  private scheduleInitialLoadRetry() {
    if (this.initialLoadAttempts >= 5) return;
    this.initialLoadAttempts += 1;
    const delayMs = 1200 * this.initialLoadAttempts;
    if (this.initialLoadTimer !== null) {
      clearTimeout(this.initialLoadTimer);
    }
    this.initialLoadTimer = window.setTimeout(() => {
      const vm: any = this as any;
      if (vm && (vm._isBeingDestroyed || vm._isDestroyed)) {
        return;
      }
      this.loadInitialMessages();
    }, delayMs);
  }

  private async loadInitialMessages() {
    if (!this.open) return;

    const p = PlayerStore.getPlayer();
    if (!p) {
      this.scheduleInitialLoadRetry();
      return;
    }

    if (this.isClanRoom) {
      if (!p.clanId || p.level < this.clanMinLevel) return;

      if (!ClanStore.getClanInfo() && !this.clanInfoLoadAttempted) {
        this.clanInfoLoadAttempted = true;
        try {
          await ClanStore.fetchMyClan();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('pc side clan chat: failed to fetch clan info', e);
        }
      }
    }

    try {
      await this.fetchChatList({ roomType: this.roomType });
      this.initialLoadAttempts = 0;
      this.$nextTick(() => this.scrollToBottom());
    } catch (e) {
      if (!this.isExpectedInitialLoadError(e)) {
        // eslint-disable-next-line no-console
        console.log('pc side chat initial load failed', this.roomType, e);
      }
      this.scheduleInitialLoadRetry();
    }
  }

  private onDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const root = this.$el as HTMLElement | undefined;
    if (!root) return;
    if (!root.contains(target)) {
      this.smileyPickerOpen = false;
    }
  };

  private syncLiveUpdatesState() {
    if (!this.open) {
      this.liveUpdates.stop();
      return;
    }

    this.liveUpdates.start();
    this.liveUpdates.trigger();
  }

  private async syncLatestMessages() {
    if (!this.open) return;

    if (this.isClanRoom) {
      const p = PlayerStore.getPlayer();
      if (!p || !p.clanId || p.level < this.clanMinLevel) {
        return;
      }
    }

    const shouldStickToBottom = this.isNearBottom();
    await syncChatMessages({
      hasMessages: this.messages.length > 0,
      shouldStickToBottom,
      fetchChatList: () => this.fetchChatList({ roomType: this.roomType }),
      fetchLatestChatMessages: () => this.fetchLatestChatMessages({ roomType: this.roomType }),
      defer: (callback) => this.$nextTick(callback),
      scrollToBottom: () => this.scrollToBottom(),
    });
  }

  private closeMessageOptions() {
    const messageOptions = document.getElementsByClassName('open');
    if (messageOptions.length > 0) {
      if (messageOptions.length > 1) {
        // в игре может быть открыто несколько панелей; просто закрываем все
        for (let i = messageOptions.length - 1; i >= 0; i--) {
          messageOptions[i].classList.remove('open');
        }
        return;
      }
      messageOptions[0].classList.remove('open');
    }
  }

  private toggleSmileyPicker() {
    if (!this.canWrite) return;
    this.smileyPickerOpen = !this.smileyPickerOpen;
  }

  private appendSmiley(s: string) {
    this.text = `${this.text}${s}`;
    this.smileyPickerOpen = false;
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (!this.canWrite) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  private async handleSend() {
    if (!this.canWrite) return;
    let text = this.text.trim();
    if (!text) return;

    try {
      if (this.quotedMessage) {
        const forwardedMessage = this.quotedMessage;
        let forwardedMessageText = forwardedMessage.text || '';
        if (forwardedMessageText.includes(this.forwardedMessagePrefix)) {
          const splitMessage = forwardedMessageText.split(this.forwardedMessagePrefix);
          forwardedMessageText = splitMessage[splitMessage.length - 1];
        }
        const forwardedMessageName = forwardedMessage.authorName;
        text = `${forwardedMessageName}${this.forwardedMessagePrefix}${forwardedMessageText}${this.forwardedMessagePrefix}${text}`;
        this.clearQuote();
      }

      await this.postChatMessage({ roomType: this.roomType, text });
      this.text = '';

      if (this.messages.length) {
        await this.fetchLatestChatMessages({ roomType: this.roomType });
      } else {
        await this.fetchChatList({ roomType: this.roomType });
      }
      this.$nextTick(() => this.scrollToBottom());
    } catch (e) {
      // не падаем — сервер может отклонить (мут/уровень и т.п.)
      // лог оставляем для дебага
      // eslint-disable-next-line no-console
      console.log('chat send error', e);
    }
  }

  private setChatListener() {
    const listener = (chatRoom: string, n: ChatNotification) => {
      const player = PlayerStore.getPlayer();
      if (!player) {
        return;
      }
      if (chatRoom === this.roomType && n.senderId !== player.id) {
        this.fetchLatestChatMessages({ roomType: this.roomType }).then(() => {
          this.$nextTick(() => this.scrollToBottomIfNearBottom());
        });
      }
    };
    NotificationManager.addChatNotificationListener(listener);
    this.$once('hook:beforeDestroy', () => {
      NotificationManager.removeChatNotificationListener(listener);
    });
  }

  private setMessageDeleteListener() {
    const listener = (chatRoom: string, n: ChatMessageDeleteNotification) => {
      if (chatRoom === this.roomType) {
        this.$store.commit('DELETE_CHAT_MESSAGE', { roomType: chatRoom, id: n.messageId });
        const el = document.getElementById(`chat-message-${n.messageId}`);
        if (el) el.remove();
      }
    };
    NotificationManager.addChatMessageDeleteNotificationListener(listener);
    this.$once('hook:beforeDestroy', () => {
      NotificationManager.removeChatMessageDeleteNotificationListener(listener);
    });
  }

  private handleScroll() {
    const el = this.$refs.messagesArea as HTMLElement | undefined;
    if (!el) return;
    if (el.scrollTop <= 20) {
      // подгружаем более старые
      this.fetchChatList({ roomType: this.roomType });
    }
  }

  private scrollToBottom() {
    const el = this.$refs.messagesArea as HTMLElement | undefined;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  private isNearBottom() {
    const el = this.$refs.messagesArea as HTMLElement | undefined;
    if (!el) return true;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    return distance < 120;
  }

  private scrollToBottomIfNearBottom() {
    const el = this.$refs.messagesArea as HTMLElement | undefined;
    if (!el) return;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    if (distance < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }

  private scrollBy(delta: number) {
    const el = this.$refs.messagesArea as HTMLElement | undefined;
    if (!el) return;
    el.scrollTop += delta;
  }

  private onQuote(message: MessageChat) {
    this.quotedMessage = message;
  }

  private onInsertNickname(name: string) {
    if (!this.canWrite) return;
    // стрелочка должна вставлять ник и НЕ показывать блок цитирования
    this.clearQuote();

    const snippet = `${name}, `;
    const el = this.$refs.chatInput as HTMLTextAreaElement | undefined;
    if (!el) {
      this.text = `${snippet}${this.text}`;
      return;
    }

    const start = el.selectionStart ?? this.text.length;
    const end = el.selectionEnd ?? this.text.length;
    const before = this.text.slice(0, start);
    const after = this.text.slice(end);
    this.text = `${before}${snippet}${after}`;

    this.$nextTick(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  }

  private async onDelete(messageId: number) {
    try {
      await this.deleteChatMessage({ roomType: this.roomType, id: messageId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('chat delete error', e);
    }
  }
}
